import { decompressFrames, parseGIF } from 'gifuct-js';
import logger from 'src/logger';
import { getAspectRatio } from 'src/utils';
import AnimationsManager from 'src/sdk-components/AnimationsManager';
import { disposeMesh } from 'src/utils/object3DHelpers';
import { getComposedTexture } from '../texture';
import { Inputs, Outputs, ComponentInteractionType, SceneComponent, LoadingState } from './types';

export class GIFLoaderComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private model: any = null;

  private size: [number, number];

  private animationsManager: AnimationsManager | null;

  private composedTexture: any = null;

  public animate = false;

  public autoplay = false;

  inputs: Inputs = {
    url: null,
    width: null,
    height: null,
    enableHoverAnimation: false,
    animationPulse: false,
    animationUpDown: false,
    animationFlip: false,
    adminPanelAccess: false
  };

  outputs = {
    loadingState: LoadingState.Idle
  } as Outputs;

  // setting which events are compatible with the component
  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false
  };

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;
    this.root.add(this.pivot);
    this.animationsManager = new AnimationsManager(THREE, this.root.parent);
    if (this.inputs.adminPanelAccess) {
      this.animate = true;
      this.autoplay = true;
    }

    getAspectRatio(this.inputs.url)
      .then(([w, h]) => {
        this.size = [w / 100, h / 100]; // scale down from pixels to meters
        this.loadModel();
      })
      .catch(() => {
        logger.error('Failed to init gif');
        this.outputs.loadingState = LoadingState.Error;
      });
  }

  onDestroy() {
    if (this.model) {
      this.model.parent?.remove(this.model);
      // @ts-ignore
      this.model?.material?.dispose();
      // @ts-ignore
      this.model?.geometry?.dispose();
      this.model = null;
    }
  }

  cycleVR = (_time, delta) => {
    if (this.animate) {
      if (this.composedTexture && !this.composedTexture.isPlaying) {
        this.composedTexture.play();
      }
      if (this.composedTexture) {
        this.composedTexture.update(delta / 1000);
      }
      if (this.animationsManager) {
        this.animationsManager.update(delta);
      }
    } else if (!this.animate && this.composedTexture?.isPlaying) {
      this.composedTexture.stop();
    }
  };

  private loadModel = () => {
    const THREE = this.context.three;
    const fileLoader = new THREE.FileLoader();
    const [w, h] = this.size;
    const { width, height, url } = this.inputs;

    if (this.model) {
      disposeMesh(this.model);
      this.model = null;
    }

    if (!url) return;

    this.outputs.loadingState = LoadingState.Loading;

    fileLoader.responseType = 'arraybuffer';
    fileLoader.load(this.inputs.url, async (data: ArrayBuffer) => {
      const gif = parseGIF(data as ArrayBuffer);

      const container = {
        downscale: true,
        width: gif.lsd.width,
        height: gif.lsd.height,
        frames: await decompressFrames(gif, true),
        autoplay: this.autoplay
      };

      const THREEComposedTexture = getComposedTexture(
        THREE,
        this.context.renderer.capabilities.maxTextureSize
      );
      // @ts-ignore
      this.composedTexture = new THREEComposedTexture(container);

      this.composedTexture.encoding = THREE.LinearEncoding;

      const geometry = new THREE.PlaneBufferGeometry(width || w, height || h);
      const material = new THREE.MeshBasicMaterial({
        map: this.composedTexture,
        transparent: true,
        alphaTest: 0,
        side: THREE.DoubleSide
      });

      this.model = new THREE.Mesh(geometry, material);

      this.pivot?.add(this.model);
      this.outputs.loadingState = LoadingState.Loaded;

      this.playAnimation();
    });
  };

  public playAnimation() {
    const THREE = this.context.three;

    if (!this.animationsManager) {
      this.animationsManager = new AnimationsManager(THREE, this.root.parent);
    }
    if (this.inputs.animationPulse) {
      this.animationsManager.pulse();
      this.animationsManager.play();
    } else if (this.inputs.animationUpDown) {
      this.animationsManager.bounceUpDown();
      this.animationsManager.play();
    } else if (this.inputs.animationFlip) {
      this.animationsManager.flip();
      this.animationsManager.play();
    }
  }

  toggleHoverAnimation(value: boolean) {
    if (this.animationsManager && this.inputs.enableHoverAnimation) {
      if (value) {
        if (this.animationsManager) {
          this.animationsManager.stop();
          this.animationsManager.outlinePulse();
          this.animationsManager.play();
        }
      } else {
        this.animationsManager.stop();

        if (this.inputs.animationPulse) {
          this.animationsManager.pulse();
          this.animationsManager.play();
        } else if (this.inputs.animationUpDown) {
          this.animationsManager.bounceUpDown();
          this.animationsManager.play();
        } else if (this.inputs.animationFlip) {
          this.animationsManager.flip();
          this.animationsManager.play();
        }
      }
    }
  }

  stopAnimation() {
    if (this.animationsManager) {
      this.animationsManager.stop();
      this.animationsManager = null;
    }
  }

  onTick(delta: number) {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame((timestamp) => {
        this.cycleVR(timestamp, delta);
      });
    }
    if (this.animate) {
      if (this.composedTexture && !this.composedTexture.isPlaying) {
        this.composedTexture.play();
      }
      if (this.composedTexture) {
        this.composedTexture.update(delta / 1000);
      }
      if (this.animationsManager) {
        this.animationsManager.update(delta);
      }
    } else if (!this.animate && this.composedTexture?.isPlaying) {
      this.composedTexture.stop();
    }
  }
}

export const GIFLoaderComponentType = 'mp.GIFLoaderComponent';
export const makeGIFLoaderComponent = () => new GIFLoaderComponent();
